import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  RiskCalculatorService,
  riskCalculatorKeys,
} from '@client/services/riskCalculatorService';

/**
 * Hook to fetch cultures tree for risk calculator
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with cultures tree data
 */
export const useCulturesTree = (options = {}) => {
  return useQuery({
    queryKey: riskCalculatorKeys.culturesTree(),
    queryFn: () => RiskCalculatorService.getCulturesTree(),
    staleTime: 30 * 60 * 1000, // 30 minutes - cultures don't change often
    gcTime: 60 * 60 * 1000, // 1 hour (React Query v5)
    ...options,
  });
};

/**
 * Hook to fetch treatment types
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with treatment types
 */
export const useTreatmentTypes = (options = {}) => {
  return useQuery({
    queryKey: riskCalculatorKeys.treatmentTypes(),
    queryFn: () => RiskCalculatorService.getTreatmentTypes(),
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000, // 1 hour (React Query v5)
    ...options,
  });
};

/**
 * Hook to fetch application techniques
 * @param {Object} params - Query parameters
 * @param {string} [params.treatment_type] - Filter by treatment type
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with application techniques
 */
export const useApplicationTechniques = (params = {}, options = {}) => {
  return useQuery({
    queryKey: riskCalculatorKeys.applicationTechnique(params),
    queryFn: () => RiskCalculatorService.getApplicationTechniques(params),
    enabled: options.enabled !== false,
    staleTime: 30 * 60 * 1000, // 30 minutes
    ...options,
  });
};

/**
 * Hook to search commercial products
 * @param {Object} params - Search parameters
 * @param {string} [params.search] - Search term
 * @param {Array<string>} [params.cultures] - Filter by culture IDs
 * @param {string} [params.treatment_type] - Filter by treatment type
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with commercial products
 */
export const useCommercialProducts = (params = {}, options = {}) => {
  return useQuery({
    queryKey: riskCalculatorKeys.productsSearch(params),
    queryFn: () => RiskCalculatorService.searchCommercialProducts(params),
    enabled: !!params.search || params.enabled !== false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

/**
 * Hook to fetch units for product dosage
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with units
 */
export const useUnits = (options = {}) => {
  return useQuery({
    queryKey: riskCalculatorKeys.units(),
    queryFn: () => RiskCalculatorService.getUnits(),
    staleTime: 60 * 60 * 1000, // 1 hour - units don't change often
    gcTime: 2 * 60 * 60 * 1000, // 2 hours (React Query v5)
    ...options,
  });
};

/**
 * Hook to calculate risk indices (mutation)
 * @param {Object} [options={}] - Additional React Query mutation options
 * @returns {Object} React Query mutation result
 * @example
 * const { mutate: calculateRisks, isPending } = useCalculateRisks({
 *   onSuccess: (data) => console.log('Results:', data),
 *   onError: (error) => console.error('Error:', error),
 * });
 * calculateRisks(payload);
 */
export const useCalculateRisks = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload) => RiskCalculatorService.calculateRisks(payload),
    onSuccess: (data, variables) => {
      // Cache the calculation result
      queryClient.setQueryData(riskCalculatorKeys.calculation(variables), data);
    },
    ...options,
  });
};

/**
 * Hook to save a scenario (mutation)
 * @param {Object} [options={}] - Additional React Query mutation options
 * @returns {Object} React Query mutation result
 */
export const useSaveScenario = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scenario) => RiskCalculatorService.saveScenario(scenario),
    onSuccess: () => {
      // Invalidate scenarios list to refresh
      queryClient.invalidateQueries(riskCalculatorKeys.scenarios());
    },
    ...options,
  });
};

/**
 * Hook to fetch saved scenarios
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with scenarios
 */
export const useScenarios = (options = {}) => {
  return useQuery({
    queryKey: riskCalculatorKeys.scenarios(),
    queryFn: () => RiskCalculatorService.getScenarios(),
    staleTime: 1 * 60 * 1000, // 1 minute
    ...options,
  });
};

/**
 * Hook to fetch cascading select options
 * Used for treatment types, application techniques, products, and units
 *
 * @param {Object} params - Query parameters
 * @param {string} params.step - Step type: 'treatment_type', 'application_technique', 'products', 'units'
 * @param {Array<string>} [params.cultures] - Selected culture IDs
 * @param {string} [params.treatmentType] - Selected treatment type
 * @param {string} [params.applicationTechnique] - Selected application technique
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with options
 */
export const useCascadingSelectOptions = (params = {}, options = {}) => {
  const { step, cultures, treatmentType, applicationTechnique } = params;

  // Build query params for the API
  const queryParams = {
    step,
    cultures: cultures || [],
    treatment_type: treatmentType || null,
    application_technique: applicationTechnique || null,
  };

  return useQuery({
    queryKey: ['riskCalculator', 'cascadingOptions', step, queryParams],
    queryFn: () => RiskCalculatorService.getCascadingOptions(queryParams),
    enabled: options.enabled !== false && !!step,
    staleTime: 10 * 60 * 1000, // 10 minutes - options change based on context
    gcTime: 30 * 60 * 1000, // 30 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    ...options,
  });
};

/**
 * Hook for bulk validation of selections.
 * Validates all current selections against available options in a single API call.
 * Used for proactive mismatch detection in edit/duplicate mode.
 *
 * @param {Object} selections - Current selections to validate
 * @param {Array<string>} selections.cultures - Selected culture IDs
 * @param {string} [selections.treatmentType] - Selected treatment type ID
 * @param {string} [selections.applicationTechnique] - Selected application technique ID
 * @param {Array<Object>} [selections.commercialProducts] - Selected products
 * @param {Object} [options={}] - Additional React Query options
 * @returns {Object} React Query result with validation data
 */
export const useValidateSelections = (selections = {}, options = {}) => {
  // Create a stable key based on selections
  const selectionsKey = {
    cultures: selections.cultures || [],
    treatmentType: selections.treatmentType || null,
    applicationTechnique: selections.applicationTechnique || null,
    productIds: (selections.commercialProducts || []).map((p) => p.id).sort(),
    unitIds: (selections.commercialProducts || [])
      .map((p) => p.unit)
      .filter(Boolean)
      .sort(),
  };

  return useQuery({
    queryKey: riskCalculatorKeys.validation(selectionsKey),
    queryFn: () => RiskCalculatorService.validateSelections(selections),
    enabled: options.enabled !== false && selections.cultures?.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes - validation should be relatively fresh
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    ...options,
  });
};
